import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/axiosConfig";
import { removeToken } from "../api/auth";
import { usePurchases } from "../api/usePurchases";

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "orders", label: "Orders" },
  { id: "courses", label: "Courses purchased" },
  { id: "profile", label: "User profile" },
];

const rupee = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const active = TABS.some((tab) => tab.id === requested) ? requested : "dashboard";
  const { orders, loading, error } = usePurchases();
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/me")
      .then((res) => {
        if (!cancelled) setProfile(res.data.user || null);
      })
      .catch(() => {
        if (!cancelled) setProfileError("Unable to load your profile.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const courses = useMemo(() => {
    const seen = new Set();
    return orders.filter((order) => {
      const key = String(order.course_id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [orders]);

  const displayName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") || "there";

  const openTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "dashboard") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next);
  };

  const logout = () => {
    removeToken();
    navigate("/login", { replace: true });
  };

  return (
    <main className="section-space account-page">
      <div className="container">
        <h1>My account</h1>
        <div className="account-layout">
          <nav className="account-nav" aria-label="Account">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={active === tab.id ? "is-active" : undefined}
                aria-current={active === tab.id ? "page" : undefined}
                onClick={() => openTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            <button type="button" onClick={logout}>
              Logout
            </button>
          </nav>

          <section className="account-panel">
            {active === "dashboard" ? (
              <>
                <p>
                  Hello <strong>{displayName}</strong>
                </p>
                <p>
                  From your account dashboard you can view your orders, open the courses
                  you have purchased, and check your profile.
                </p>
                <div className="account-stats">
                  <button type="button" onClick={() => openTab("orders")}>
                    <span>Orders</span>
                    <strong>{loading ? "…" : orders.length}</strong>
                  </button>
                  <button type="button" onClick={() => openTab("courses")}>
                    <span>Courses purchased</span>
                    <strong>{loading ? "…" : courses.length}</strong>
                  </button>
                </div>
              </>
            ) : null}

            {active === "orders" ? (
              <>
                <h2>Orders</h2>
                {error ? <p className="account-error">{error}</p> : null}
                {loading ? <p>Loading orders...</p> : null}
                {!loading && !error && orders.length === 0 ? (
                  <p>
                    No orders yet. <Link to="/courses">Browse courses</Link>
                  </p>
                ) : null}
                {!loading && orders.length > 0 ? (
                  <div className="account-table-wrap">
                    <table className="account-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Course</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.id}>
                            <td>#{order.id}</td>
                            <td>{order.course_title}</td>
                            <td>{formatWhen(order.paidAt || order.createdAt)}</td>
                            <td>Paid</td>
                            <td>
                              {rupee(order.total_amount)}
                              <span>
                                {rupee(order.price)} + {order.tax_percentage}% tax
                              </span>
                            </td>
                            <td>
                              <Link
                                className="btn-view-course"
                                to={`/my-courses/${order.course_id}`}
                              >
                                View course
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </>
            ) : null}

            {active === "courses" ? (
              <>
                <h2>Courses purchased</h2>
                {error ? <p className="account-error">{error}</p> : null}
                {loading ? <p>Loading courses...</p> : null}
                {!loading && !error && courses.length === 0 ? (
                  <p>
                    You have not purchased a course yet. <Link to="/courses">Browse courses</Link>
                  </p>
                ) : null}
                {!loading && courses.length > 0 ? (
                  <div className="account-course-list">
                    {courses.map((course) => (
                      <article key={course.course_id} className="account-course">
                        <div>
                          <h3>{course.course_title}</h3>
                          <p>Purchased {formatWhen(course.paidAt || course.createdAt)}</p>
                        </div>
                        <Link className="btn-view-course" to={`/my-courses/${course.course_id}`}>
                          View course
                        </Link>
                      </article>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}

            {active === "profile" ? (
              <>
                <h2>User profile</h2>
                {profileError ? <p className="account-error">{profileError}</p> : null}
                {!profile && !profileError ? <p>Loading profile...</p> : null}
                {profile ? (
                  <dl className="account-profile">
                    <div>
                      <dt>First name</dt>
                      <dd>{profile.firstName || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>Last name</dt>
                      <dd>{profile.lastName || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{profile.email || "Not set"}</dd>
                    </div>
                  </dl>
                ) : null}
              </>
            ) : null}
          </section>
        </div>
      </div>
    </main>
  );
}

export default Dashboard;
